import { Link, useNavigate, useLocation } from "react-router-dom";
import { User, LogOut, Menu, Shield, Home, Settings } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { signOut } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useProduct, ProductId } from "@/hooks/useProduct";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { cn } from "@/lib/utils";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useCurrentUser, useProfile } from "@/hooks/useProfileQueries";

interface AuthenticatedHeaderProps {
  isAdmin?: boolean;
}

export function AuthenticatedHeader({ isAdmin = false }: AuthenticatedHeaderProps) {
  const { t } = useTranslation();
  const { productId, config } = useProduct();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { openProfileModal } = useProfileModal();
  const { data: user } = useCurrentUser();
  const { data: profile } = useProfile(user?.id);

  const pathPrefix = config.routing.pathPrefix;

  const navLinks = [
    {
      key: "home",
      labelKey: "common:nav.home",
      fallback: t('common.authenticatedheader.home', 'Home'),
      path: `${pathPrefix}/dashboard`,
    },
    {
      key: "library",
      labelKey: "common:nav.library",
      fallback: t('common.authenticatedheader.library', 'Library'),
      path: `${pathPrefix}/library`,
    },
    {
      key: "my-progress",
      labelKey: "common:nav.myStats",
      fallback: t('common.authenticatedheader.myStats', 'My Stats'),
      path: `${pathPrefix}/my-progress`,
    },
  ];

  const isActive = (link: typeof navLinks[0]) => {
    const currentPath = location.pathname;

    if (link.key === "home") {
      return currentPath.includes("/dashboard");
    }
    return currentPath.includes(link.path);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md shadow-[0_4px_12px_-2px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.4),0_2px_6px_-2px_rgba(0,0,0,0.3)]">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Product Name */}
          <Link to={`${pathPrefix}/dashboard`} className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                background: `linear-gradient(to bottom right, ${config.branding.primaryColor}, ${config.branding.secondaryColor})`,
              }}
            >
              <span className="text-white font-bold text-lg">
                {productId === ProductId.MINDCOTINE ? "M" : "R"}
              </span>
            </div>
            <span className="text-lg font-semibold text-foreground hidden sm:inline">
              {config.name}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.path}
                className={cn(
                  "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                  isActive(link)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {t(link.labelKey, link.fallback)}
              </Link>
            ))}
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-2 gap-2"
                  onClick={() => navigate("/")}
                >
                  <Home className="h-4 w-4" />
                  {t("common:nav.platformHome", "Platform")}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="gap-2"
                  onClick={() => navigate("/admin", { state: { fromProduct: productId } })}
                >
                  <Shield className="h-4 w-4" />
                  {t("common:nav.adminPanel", "Admin Panel")}
                </Button>
              </>
            )}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage key={profile?.avatar_url || 'no-avatar'} src={profile?.avatar_url || ''} alt="Profile" />
                    <AvatarFallback className="bg-transparent">
                      <User className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="sr-only">{t("common:nav.profile", "Profile")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={openProfileModal}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t("common:nav.profile", "Profile")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("common:auth.signOut", "Sign Out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Actions */}
          <div className="flex lg:hidden items-center gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t("common:nav.menu", "Menu")}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-72">
          <SheetHeader>
            <SheetTitle>{config.name}</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-4 mt-6">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                openProfileModal();
              }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <Settings className="h-5 w-5" />
              <span className="font-medium">{t("common:nav.profile", "Profile")}</span>
            </button>
            
            {isAdmin && (
              <>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate("/");
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <Home className="h-5 w-5" />
                  <span className="font-medium">{t("common:nav.platformHome", "Platform")}</span>
                </button>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate("/admin", { state: { fromProduct: productId } });
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <Shield className="h-5 w-5" />
                  <span className="font-medium">{t("common:nav.adminPanel", "Admin Panel")}</span>
                </button>
              </>
            )}

            <div className="border-t border-border my-2" />

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleSignOut();
              }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-left text-destructive"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">{t("common:auth.signOut", "Sign Out")}</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
